import React, { useState, useEffect, useMemo } from 'react';
import { Stack, Group, Box } from '@mantine/core';
import { ChartWithTimelines } from '.';
import { DateRangeSelector } from './DateRangeSelector';
import { MetricsSelector } from './MetricsSelector';
import { LabMetricsSelector } from './LabMetricsSelector';
import { AppMedWarning } from './AppMedWarning';
import { useParsedDates } from '../utils';
import { useGetPhysiologicalTypes } from '../../../../global/entities/PhysiologicalData/queries/useGetPhysiologicalTypes';
import { useGetphysiologicalData } from '../../../../global/entities/PhysiologicalData/queries/useGetPhysiologicalDatas';
import { useDateRange, useChartData } from '../hooks';
import { t } from 'i18next';
import { CustomDateModal } from './CustomDateModal';
import { PhysiologicalType } from '../../../../global/entities/PhysiologicalData/PhysiologicalData';
import { observer } from 'mobx-react';
import { inject } from 'mobx-react';
import { PatientStore } from '../../../../global/Store/PatientStore';
import { useGetLabTests } from '../../../../global/entities/LabTest/queries/useGetLabTests';
import { saveSelectedMetrics, getSelectedMetrics, VITALS_METRICS_KEY } from '../utils/localStorage';
import { ILabTest } from '../../../../global/entities/LabTest/LabTest';
import { processLabTestData } from '../utils';
import { getChartDataHelpers, groupLabTests, groupPhysiologicalMetrics } from '../utils';
import { useGetBiomarkers } from '../../../../global/entities/Biomarker/queries/useGetBiomarkers';

interface TransformedChartDataPoint {
  FormattedDate: string;
  ShortDate: string;
  [key: string]: string | number | null; // For dynamic physiological type values
}

const BODY_TEMPERATURE_TYPE = 4;
const WEIGHT_TYPE = 8;

// Unit conversion functions
const convertTemperature = (value: number, fromUnit: number, toUnit: number) => {
  if (fromUnit === toUnit) return value;
  if (fromUnit === 34 && toUnit === 29) 
    return (value - 32) * 5/9;
  
  if (fromUnit === 29 && toUnit === 34) 
    return (value * 9/5) + 32;
  
  return value;
};

interface PhysiologicalData {
  physiologicalTypeID: number;
  dateMeasured: string | Date;
  value: string;
  unitID: number;
}

/**
 * Transforms raw physiological data and lab test data into a chart-friendly format.
 *
 * For each date, aggregates all physiological measurements into a single object,
 * with keys for each metric (e.g., "typeId-abbreviation"). Handles both single-value
 * and multi-component measurements (e.g., blood pressure "120/80"). Converts units
 * to preferred units if needed.
 *
 * @param physiologicalData - Array of physiological data points (with type, date, value)
 * @param physiologicalTypes - Array of physiological type metadata (units, components, etc.)
 * @param labTests - Array of lab test data points (with date, description, specimen, unit, value)
 * @returns Array of objects, one per date, each containing all physiological values for that date
 */
function transformHealthMetricsData(
  physiologicalData: PhysiologicalData[],
  physiologicalTypes: PhysiologicalType[],
  labTests: ILabTest[],
  startTime: string,
  endTime: string,
  selectedPkMetrics: string[] = [],
  selectedExternalMetrics: string[] = [],
  pkBiomarkerIds: string[] = []
) {
  if (!physiologicalData || !physiologicalTypes) return [];

  // Map type IDs to their metadata for quick lookup
  const typeIdToType = new Map(physiologicalTypes.map(type => [type.id, type]));
  const dataByDate: Record<string, TransformedChartDataPoint> = {};
  const { addValue, formatDateKey } = getChartDataHelpers(dataByDate);

  // --- Process physiological data ---
  for (const dataPoint of physiologicalData) {
    const dateObj = new Date(dataPoint.dateMeasured);
    const dateKey = formatDateKey(dateObj);
    const type = typeIdToType.get(dataPoint.physiologicalTypeID);
    if (!type) continue;

    const currentUnit = dataPoint.unitID;
    const preferredUnit = type.preferredUnit ?? currentUnit;
    const key = `${type.id}-${type.abbreviation}`;

    // Multi-component (e.g., blood pressure)
    if (type.subtypes && type.subtypes.split(',').length > 1) {
      const valueParts = typeof dataPoint.value === 'string' ? dataPoint.value.split('/') : [];
      const parts = type.subtypes.split(',');
      parts.forEach((typeId, idx) => {
        const subtype = physiologicalTypes.find(t => t.id === parseInt(typeId));
        if (subtype) {
          const subKey = `${subtype.id}-${subtype.abbreviation}`;
          addValue(dateKey, dateObj, subKey, valueParts[idx] ? parseFloat(valueParts[idx]) : null);
        }
      });
    } else {
      // Single-value types
      let value = parseFloat(dataPoint.value);
      if (preferredUnit && currentUnit !== preferredUnit) {
        if (type.id === BODY_TEMPERATURE_TYPE) 
          value = convertTemperature(value, currentUnit, preferredUnit);
         else if (type.id === WEIGHT_TYPE) 
          value = value * 0.45359237; // lbs to kg
        
      }
      addValue(dateKey, dateObj, key, value);
    }
  }

  // --- Process lab test data ---
  const selectedLabMetrics = [
    ...selectedPkMetrics,
    ...selectedExternalMetrics
  ];
  
  processLabTestData({
    labTests,
    startTime,
    endTime,
    selectedLabMetrics,
    pkBiomarkerIds,
    addValue,
    formatDateKey,
  });

  // --- Return sorted array of date-grouped data points ---
  return Object.values(dataByDate).sort(
    (a, b) => new Date(a.FormattedDate).getTime() - new Date(b.FormattedDate).getTime()
  );
}

export type IPhysiologicalDataTableProps = {
  patientStore?: PatientStore;
};

export const PhysiologicalsTab: React.FC<IPhysiologicalDataTableProps> = inject(
  'patientStore',
  'routeStore',
)(
  observer(({ patientStore }) => {
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [selectedPkMetrics, setSelectedPkMetrics] = useState<string[]>([]);
  const [selectedExternalMetrics, setSelectedExternalMetrics] = useState<string[]>([]);
  const [physiologicalMetricsOptions, setPhysiologicalMetricsOptions] = useState<{ value: string; label: string }[]>([]);
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const { data: physiologicalData } = useGetphysiologicalData(patientStore?.patientId);
  const { data: physiologicalTypes } = useGetPhysiologicalTypes();
  const { data: fetchedLabTests } = useGetLabTests(patientStore?.patientId);
  const { data: biomarkers } = useGetBiomarkers();
  const labTests = useMemo(() => [...(fetchedLabTests || [])], [fetchedLabTests]);
  
  // Calculate PK biomarker IDs for time averaging
  const pkBiomarkerIds = useMemo(() => {
    if (!biomarkers) return [];
    return biomarkers
      .filter(biomarker => biomarker.isPK)
      .map(biomarker => `lab-${biomarker.id}`);
  }, [biomarkers]);
  const chartMargins = { top: 5, right: 30, left: 20, bottom: 5 };
  
  const physiologicalMetrics = useMemo(() => {
    if (!physiologicalTypes) return [];
    return groupPhysiologicalMetrics(physiologicalTypes);
  }, [physiologicalTypes]);

  const labMetrics = useMemo(() => {
    return groupLabTests(labTests);
  }, [labTests]);
  
  const transformedChartData = useMemo(
    () => transformHealthMetricsData(
      physiologicalData || [], 
      physiologicalTypes || [], 
      labTests, 
      startTime, 
      endTime, 
      selectedPkMetrics,
      selectedExternalMetrics,
      pkBiomarkerIds
    ),
    [physiologicalData, physiologicalTypes, labTests, startTime, endTime, selectedPkMetrics, selectedExternalMetrics, pkBiomarkerIds]
  );

  const parsedDates = useParsedDates(transformedChartData);

  useEffect(() => {
    const allOptions = [
      ...physiologicalMetrics.map(metric => ({ value: metric.id.toString(), label: metric.name})),
    ].sort((a, b) => a.label.localeCompare(b.label));

    setPhysiologicalMetricsOptions(allOptions);
  }, [physiologicalMetrics]);

  const {
    dateRangePreset,
    customDateRange,
    setShowCustomDateModal,
    showCustomDateModal,
    dateRange,
    handleDateRangeChange,
    handleCustomDateChange,
    dateRangePresets
  } = useDateRange({ parsedDates });

  // Use chart data processing hook
  const {
    processedChartData,
    allDates,
    getDatePosition,
  } = useChartData({
    chartData: transformedChartData,
    dateRange,
    parsedDates
  });
  
  // Determine if we should display months instead of days for large date ranges
  const dateRangeDuration = dateRange.endDate && dateRange.startDate ? 
    Math.floor((dateRange.endDate.getTime() - dateRange.startDate.getTime()) / (1000 * 60 * 60 * 24)) : 0;
  const xAxisFormat = dateRangeDuration > 90 ? 'month' : 'day';
  
  // Set initial selected metrics when metrics are loaded
  useEffect(() => {
    if (physiologicalMetrics && physiologicalMetrics.length > 0) {
      const savedMetrics = getSelectedMetrics(VITALS_METRICS_KEY);
      if (savedMetrics.length > 0) {
        // Filter out any saved metrics that no longer exist
        const validSavedMetrics = savedMetrics.filter(metricId => 
          physiologicalMetrics.some(metric => metric.id.toString() === metricId)
        );
        setSelectedMetrics(validSavedMetrics);
      }
    }
  }, [physiologicalMetrics]);

  const handleMetricsChange = (selected: string[]) => {
    setSelectedMetrics(selected);
    saveSelectedMetrics(VITALS_METRICS_KEY, selected);
  };

  return (
    <Stack gap="md">
      <Group align="flex-start" gap="lg">
      <AppMedWarning />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Stack gap="md">
            <DateRangeSelector
              dateRangePreset={dateRangePreset}
              onDateRangeChange={handleDateRangeChange}
              customDateRange={customDateRange}
              setShowCustomDateModal={setShowCustomDateModal}
              dateRangePresets={dateRangePresets}
            />
            
            <MetricsSelector
              metrics={physiologicalMetricsOptions}
              selectedMetrics={selectedMetrics}
              onChange={handleMetricsChange}
              label={t('graph.selectPhysiologicalData')}
              placeholderKey="graph.metricsSelected"
            />
        
            <LabMetricsSelector 
              selectedPkMetrics={selectedPkMetrics}
              onPkMetricsChange={setSelectedPkMetrics}
              selectedExternalMetrics={selectedExternalMetrics}
              onExternalMetricsChange={setSelectedExternalMetrics}
              startTime={startTime}
              endTime={endTime}
              onStartTimeChange={setStartTime}
              onEndTimeChange={setEndTime}
            />
          </Stack>
        </Box>
      </Group>
      
      <CustomDateModal
        opened={showCustomDateModal}
        onClose={() => setShowCustomDateModal(false)}
        dateRange={customDateRange}
        onDateChange={handleCustomDateChange}
      />
      
      <ChartWithTimelines 
        data={processedChartData}
        metrics={[...physiologicalMetrics, ...labMetrics]}
        selectedMetrics={[
          ...selectedMetrics, 
          ...selectedPkMetrics,
          ...selectedExternalMetrics
        ]}
        chartMargins={chartMargins}
        getDatePosition={getDatePosition}
        allDates={allDates}
        xAxisDataKey="FormattedDate"
        xAxisFormat={xAxisFormat}
      />
    </Stack>
  );
  })
); 